<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :fullscreen="$vuetify.breakpoint.mobile"
    max-width="760"
    scrollable
  >
    <v-card light class="ad-publish">
      <v-card-title class="ad-publish__title">
        <div>
          <div>Neue Anzeige – {{ domain }}</div>
          <div class="ad-publish__subtitle">Wird pausiert angelegt · Aktivierung im Ads Manager</div>
        </div>
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Success -->
        <div v-if="published" class="ad-publish__success">
          <v-icon color="success" size="56">mdi-check-circle-outline</v-icon>
          <div class="mt-3 font-weight-bold">
            {{ published.ads.length }} {{ published.ads.length === 1 ? "Anzeige" : "Anzeigen" }} angelegt (pausiert)
          </div>
          <div v-if="published.newAdsetCreated" class="ad-publish__hint mt-1">Neues Adset wurde erstellt (ebenfalls pausiert).</div>
          <div v-if="published.budgetIgnoredBecauseCbo" class="ad-publish__hint warning--text mt-1">
            Hinweis: Budget wird von der Kampagne gesteuert (CBO) — dein Tagesbudget wurde ignoriert.
          </div>
          <div v-if="published.carousel" class="ad-publish__hint mt-1">Als Carousel mit {{ publishImages.length }} Karten angelegt.</div>
          <div v-if="published.multiTextApplied" class="ad-publish__hint mt-1">✓ Mehrere Text-/Headline-Optionen hinterlegt — Meta testet sie automatisch.</div>
          <div v-if="published.multiTextFallback" class="ad-publish__hint warning--text mt-1">
            Hinweis: Das Adset unterstützt keine Mehrfach-Texte — erste Text-/Headline-Variante wurde verwendet.
          </div>
          <v-btn v-for="ad in published.ads" :key="ad.adId" class="mt-3" color="primary" outlined small :href="ad.link" target="_blank">
            <v-icon left small>mdi-open-in-new</v-icon>{{ ad.name }}
          </v-btn>
          <v-btn class="mt-4" text small @click="resetAll">Weitere Anzeige erstellen</v-btn>
        </div>

        <v-stepper v-else v-model="step" vertical flat class="ad-publish__stepper">
          <!-- 1. Idee finden -->
          <v-stepper-step :complete="selectedIdeaIndex !== null" step="1" editable>
            Idee finden <small v-if="selectedIdea" class="ml-1">{{ selectedIdea.titel }}</small>
          </v-stepper-step>
          <v-stepper-content step="1">
            <div class="ad-publish__hint mb-2">
              Die KI analysiert High- und Low-Performer, findet unbesetzte Bereiche und schlägt 3 Ideen vor — inkl. fertigem Prompt zum Generieren des Creatives.
            </div>
            <v-text-field
              v-model="productUrl"
              label="Produkt-URL (optional — damit die KI weiß, was beworben wird)"
              placeholder="https://artquarell.com/products/…"
              dense
              outlined
              hide-details
              class="mb-2"
            />
            <v-textarea
              v-model="ideaDirection"
              label="Richtung vorgeben (optional)"
              placeholder="z.B. Fokus Haustier-Motive, Geschenk-Anlass, UGC-Stil…"
              rows="1"
              auto-grow
              dense
              outlined
              hide-details
              class="mb-2"
            />
            <v-btn v-if="!ideasLoading" color="primary" depressed small @click="generateIdeas">
              <v-icon left small>mdi-lightbulb-on-outline</v-icon>
              {{ ideas.length ? "Neue Ideen generieren" : "Ideen generieren" }}
            </v-btn>
            <v-btn v-if="!ideasLoading" text small class="ml-2" @click="step = 2">Überspringen</v-btn>
            <v-alert v-if="ideasError" type="error" dense outlined class="mt-2">{{ ideasError }}</v-alert>

            <!-- Live-Fortschritt während der Ideen-Generierung -->
            <div v-if="ideasLoading" class="ad-publish__progress">
              <div
                v-for="(stage, stageIndex) in ideaStages"
                :key="stage"
                class="ad-publish__progress-line"
                :class="{
                  'ad-publish__progress-line--done': stageIndex < ideaStageIndex,
                  'ad-publish__progress-line--active': stageIndex === ideaStageIndex,
                }"
              >
                <v-icon v-if="stageIndex < ideaStageIndex" small color="success">mdi-check-circle</v-icon>
                <v-progress-circular v-else-if="stageIndex === ideaStageIndex" indeterminate size="15" width="2" color="primary" />
                <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
                <span>{{ stage }}</span>
              </div>
            </div>

            <div v-if="ideasAnalysis" class="ad-publish__analysis mt-3">{{ ideasAnalysis }}</div>

            <div
              v-for="(idea, index) in ideas"
              :key="index"
              class="ad-publish__idea"
              :class="{ 'ad-publish__idea--selected': selectedIdeaIndex === index }"
              @click="selectIdea(index)"
            >
              <div class="ad-publish__idea-head">
                <v-icon small :color="selectedIdeaIndex === index ? 'primary' : 'grey'">
                  {{ selectedIdeaIndex === index ? "mdi-check-circle" : "mdi-circle-outline" }}
                </v-icon>
                <span class="ad-publish__idea-title">{{ idea.titel }}</span>
                <v-chip x-small :color="idea.typ === 'luecke' ? 'deep-purple lighten-4' : 'blue lighten-4'">
                  {{ idea.typ === "luecke" ? "Lücke" : "Variation" }}
                </v-chip>
                <v-chip v-if="idea.stil" x-small outlined>
                  {{ { "text-overlay": "Mit Text", clean: "Clean", frei: "Kreativ frei" }[idea.stil] || idea.stil }}
                </v-chip>
              </div>
              <div class="ad-publish__idea-goal">{{ idea.ziel }}</div>
              <div class="ad-publish__hint">{{ idea.begruendung }}</div>
              <!-- Entwürfe (Nano Banana 2 Lite) — generiert nach Auswahl der Idee -->
              <div v-if="ideaPreviews[index]" class="ad-publish__previews" @click.stop>
                <div
                  v-for="(preview, previewIndex) in ideaPreviews[index].images"
                  :key="previewIndex"
                  class="ad-publish__preview"
                  :class="{ 'ad-publish__preview--selected': isPreviewSelected(index, previewIndex) }"
                  @click="togglePreview(index, previewIndex)"
                >
                  <img :src="`data:${preview.media_type};base64,${preview.data}`" />
                  <v-icon v-if="isPreviewSelected(index, previewIndex)" class="ad-publish__preview-check" color="primary">mdi-check-circle</v-icon>
                  <v-btn icon x-small class="ad-publish__zoom" @click.stop="openLightbox(preview)">
                    <v-icon x-small>mdi-magnify-plus-outline</v-icon>
                  </v-btn>
                </div>
                <div v-if="ideaPreviews[index].loading" class="ad-publish__preview ad-publish__preview--loading">
                  <v-progress-circular indeterminate size="20" width="2" />
                  <span>{{ ideaPreviews[index].images.length }}/3</span>
                </div>
                <div
                  v-else-if="ideaPreviews[index].images.length < 6"
                  class="ad-publish__preview ad-publish__preview--add"
                  @click="addPreview(index)"
                >
                  <v-progress-circular v-if="ideaPreviews[index].adding" indeterminate size="20" width="2" />
                  <template v-else>
                    <v-icon>mdi-plus</v-icon>
                    <span>Weiteres Bild</span>
                  </template>
                </div>
              </div>
              <div v-if="ideaPreviews[index] && ideaPreviews[index].error" class="ad-publish__hint error--text" @click.stop>
                {{ ideaPreviews[index].error }}
              </div>
              <div class="ad-publish__idea-prompt" @click.stop>
                <div class="ad-publish__idea-prompt-head">
                  <span>Bild-Prompt</span>
                  <v-btn x-small text color="primary" @click="copyPrompt(idea, index)">
                    <v-icon x-small left>{{ copiedIndex === index ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    {{ copiedIndex === index ? "Kopiert" : "Kopieren" }}
                  </v-btn>
                </div>
                {{ idea.bildPrompt }}
              </div>
            </div>

            <div v-if="ideas.length" class="ad-publish__hint mt-2">
              Idee anklicken → 3 Entwürfe werden generiert → einen oder mehrere Entwürfe auswählen → Weiter. Alternativ: Prompt kopieren und eigenes Bild in Schritt 2 hochladen.
            </div>
            <div class="ad-publish__step-actions">
              <v-btn color="primary" depressed small @click="proceedFromIdeas">
                Weiter<span v-if="selectedPreviews.length"> mit {{ selectedPreviews.length }} Entwurf{{ selectedPreviews.length > 1 ? "en" : "" }}</span>
              </v-btn>
            </div>
          </v-stepper-content>

          <!-- 2. Bilder -->
          <v-stepper-step :complete="images.length > 0" step="2" editable>
            Creative hochladen <small v-if="images.length" class="ml-1">{{ images.length }} Bild(er)</small>
          </v-stepper-step>
          <v-stepper-content step="2">
            <div class="ad-publish__images">
              <div v-for="(image, index) in images" :key="index" class="ad-publish__thumb">
                <img :src="`data:${image.full.media_type};base64,${image.full.data}`" @click="openLightbox(image.full)" />
                <v-btn icon x-small class="ad-publish__thumb-remove" @click="images.splice(index, 1)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div
                v-if="images.length < 3"
                class="ad-publish__dropzone"
                :class="{ 'ad-publish__previews--dragover': imagesDragover }"
                @click="$refs.imageInput.click()"
                @dragover.prevent="imagesDragover = true"
                @dragleave.prevent="imagesDragover = false"
                @drop.prevent="onImagesDrop"
              >
                <v-icon>mdi-image-plus</v-icon>
                <span>Bild wählen<br />oder hierher ziehen</span>
              </div>
            </div>
            <input ref="imageInput" type="file" accept="image/jpeg,image/png" multiple hidden @change="onImagesSelected" />
            <div class="ad-publish__hint">1–3 Bilder · mehrere Bilder = Varianten (V1, V2, …) im selben Adset</div>
            <div v-if="imageError" class="error--text text-caption">{{ imageError }}</div>
            <div class="ad-publish__step-actions">
              <v-btn color="primary" depressed small :disabled="!images.length" @click="step = 3">Weiter</v-btn>
            </div>
          </v-stepper-content>

          <!-- 3. KI-Analyse -->
          <v-stepper-step :complete="!!suggestions" step="3" editable>
            KI-Vorschläge <small v-if="suggestions" class="ml-1">übernommen — anpassbar in Schritt 4+5</small>
          </v-stepper-step>
          <v-stepper-content step="3">
            <div class="ad-publish__hint mb-2">
              Die KI analysiert deine Bilder zusammen mit den Performance-Daten und schlägt Copy, Format, Adset, Budget und ROAS-Ziel vor.
            </div>
            <v-btn color="primary" depressed small :loading="suggesting" :disabled="!images.length" @click="runSuggestions">
              <v-icon left small>mdi-brain</v-icon>
              {{ suggestions ? "Erneut analysieren" : "Analyse starten" }}
            </v-btn>
            <v-btn text small class="ml-2" @click="step = 4">Überspringen</v-btn>
            <v-alert v-if="suggestError" type="error" dense outlined class="mt-2">{{ suggestError }}</v-alert>
            <v-alert v-if="suggestions" color="deep-purple lighten-5" class="mt-3 ad-publish__feedback" dense>
              <div class="font-weight-bold mb-1">Creative-Feedback</div>
              <div>{{ suggestions.creativeFeedback }}</div>
              <div class="mt-2"><strong>Adset-Empfehlung:</strong> {{ suggestions.adsetReason }}</div>
            </v-alert>

            <!-- Finalisierung: Feedback → GPT Image 2 verbessert die Bilder -->
            <div v-if="suggestions && images.length" class="ad-publish__finalize">
              <template v-if="!finalImages.length">
                <v-btn color="primary" depressed small :loading="finalizing" @click="finalizeImages">
                  <v-icon left small>mdi-auto-fix</v-icon>
                  {{ finalizing ? `Finalisiere ${finalizeDone + 1}/${images.length}…` : `Finale Bilder mit GPT Image 2 erstellen (${images.length})` }}
                </v-btn>
                <span class="ad-publish__hint ml-2">wendet das Creative-Feedback auf deine Bilder an</span>
              </template>

              <template v-else>
                <div class="ad-publish__finalize-title">Finale Bilder <span class="ad-publish__hint">(Auswahl wird veröffentlicht)</span></div>
                <div
                  class="ad-publish__previews"
                  :class="{ 'ad-publish__previews--dragover': finalsDragover }"
                  @dragover.prevent="finalsDragover = true"
                  @dragleave.prevent="finalsDragover = false"
                  @drop.prevent="onFinalsDrop"
                >
                  <div
                    v-for="(finalImage, finalIndex) in finalImages"
                    :key="finalIndex"
                    class="ad-publish__preview"
                    :class="{ 'ad-publish__preview--selected': selectedFinals.includes(finalIndex) }"
                    @click="toggleFinal(finalIndex)"
                  >
                    <img :src="`data:${finalImage.full.media_type};base64,${finalImage.full.data}`" />
                    <v-icon v-if="selectedFinals.includes(finalIndex)" class="ad-publish__preview-check" color="primary">mdi-check-circle</v-icon>
                    <v-btn icon x-small class="ad-publish__zoom" @click.stop="openLightbox(finalImage.full)">
                      <v-icon x-small>mdi-magnify-plus-outline</v-icon>
                    </v-btn>
                    <v-btn icon x-small class="ad-publish__download" title="Herunterladen & lokal optimieren" @click.stop="downloadImage(finalImage, finalIndex)">
                      <v-icon x-small>mdi-download</v-icon>
                    </v-btn>
                  </div>
                  <div v-if="finalizing" class="ad-publish__preview ad-publish__preview--loading">
                    <v-progress-circular indeterminate size="20" width="2" />
                    <span>{{ finalizeDone + 1 }}/{{ finalizeTarget }}</span>
                  </div>
                  <template v-else>
                    <div v-if="finalImages.length < 6" class="ad-publish__preview ad-publish__preview--add" @click="addFinalVariant">
                      <v-icon>mdi-plus</v-icon>
                      <span>Weitere Variante</span>
                    </div>
                    <div v-if="finalImages.length < 6" class="ad-publish__preview ad-publish__preview--add" @click="$refs.finalsInput.click()">
                      <v-icon>mdi-upload</v-icon>
                      <span>Hochladen</span>
                    </div>
                  </template>
                </div>
                <input ref="finalsInput" type="file" accept="image/jpeg,image/png" multiple hidden @change="onFinalsSelected" />
                <div class="ad-publish__hint mt-1">
                  ⬇ Herunterladen → lokal optimieren → per Drag &amp; Drop oder „Hochladen" zurücklegen. Ohne Auswahl werden die Bilder aus Schritt 2 veröffentlicht.
                </div>
              </template>
              <div v-if="finalizeError" class="error--text text-caption mt-1">{{ finalizeError }}</div>
            </div>

            <div v-if="suggestions" class="ad-publish__step-actions">
              <v-btn color="primary" depressed small @click="step = 4">Weiter</v-btn>
            </div>
          </v-stepper-content>

          <!-- 4. Format -->
          <v-stepper-step :complete="step > 4" step="4" editable>
            Format <small class="ml-1">{{ formatLabel(format) }}</small>
          </v-stepper-step>
          <v-stepper-content step="4">
            <v-radio-group v-model="format" dense hide-details class="mt-0">
              <v-radio v-for="option in formatOptions" :key="option.value" :value="option.value">
                <template v-slot:label>
                  {{ option.text }}
                  <v-chip v-if="suggestions && suggestions.format === option.value" x-small color="primary" outlined class="ml-2">Vorschlag</v-chip>
                </template>
              </v-radio>
            </v-radio-group>
            <div v-if="suggestions && suggestions.formatReason" class="ad-publish__hint mt-1">{{ suggestions.formatReason }}</div>
            <div class="ad-publish__step-actions">
              <v-btn color="primary" depressed small @click="step = 5">Weiter</v-btn>
            </div>
          </v-stepper-content>

          <!-- 5. Settings -->
          <v-stepper-step :complete="step > 5" step="5" editable>Settings</v-stepper-step>
          <v-stepper-content step="5">
            <v-btn-toggle v-model="adsetMode" mandatory dense class="mb-3">
              <v-btn small value="existing">Bestehendes Adset</v-btn>
              <v-btn small value="new">Neues Adset</v-btn>
            </v-btn-toggle>

            <template v-if="adsetMode === 'existing'">
              <v-autocomplete
                v-model="form.adsetId"
                :items="adsetItems"
                :loading="adsetsLoading"
                label="Adset (Targeting & Budget werden übernommen)"
                dense
                outlined
              />
              <div v-if="recommendedAdsetLabel" class="ad-publish__hint ad-publish__pull-up">💡 Vorschlag: {{ recommendedAdsetLabel }}</div>
            </template>

            <template v-else>
              <v-text-field v-model="form.newAdsetName" label="Adset-Name" dense outlined />
              <v-autocomplete
                v-model="form.templateAdsetId"
                :items="adsetItems"
                :loading="adsetsLoading"
                label="Vorlage (Targeting & Pixel werden geklont)"
                dense
                outlined
              />
              <v-row dense>
                <v-col cols="6">
                  <v-text-field v-model.number="form.dailyBudget" label="Tagesbudget (€)" type="number" dense outlined />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="form.roasGoal"
                    label="ROAS-Mindestziel (leer = Lowest Cost)"
                    type="number"
                    step="0.1"
                    dense
                    outlined
                    :hint="breakEvenRoas ? `Break-even: ${number(breakEvenRoas)}` : ''"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              <div v-if="suggestions && suggestions.newAdset" class="ad-publish__hint ad-publish__pull-up">💡 {{ suggestions.newAdset.reasoning }}</div>
            </template>

            <v-divider class="my-3" />

            <v-text-field v-model="form.adName" label="Anzeigenname (intern)" dense outlined />

            <div class="ad-publish__field-label">
              Primary Texte ({{ form.primaryTexts.length }}/5)
              <span class="ad-publish__hint">Meta testet die Varianten automatisch gegeneinander</span>
            </div>
            <div v-for="(text, textIndex) in form.primaryTexts" :key="`pt-${textIndex}`" class="ad-publish__multi-row">
              <v-textarea v-model="form.primaryTexts[textIndex]" :label="`Text ${textIndex + 1}`" rows="2" auto-grow dense outlined hide-details />
              <v-btn v-if="form.primaryTexts.length > 1" icon x-small @click="form.primaryTexts.splice(textIndex, 1)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn v-if="form.primaryTexts.length < 5" x-small text color="primary" class="mb-3" @click="form.primaryTexts.push('')">
              <v-icon x-small left>mdi-plus</v-icon>Textoption
            </v-btn>

            <div class="ad-publish__field-label">Headlines ({{ form.headlines.length }}/3)</div>
            <div v-for="(headline, headlineIndex) in form.headlines" :key="`hl-${headlineIndex}`" class="ad-publish__multi-row">
              <v-text-field v-model="form.headlines[headlineIndex]" :label="`Headline ${headlineIndex + 1}`" dense outlined hide-details />
              <v-btn v-if="form.headlines.length > 1" icon x-small @click="form.headlines.splice(headlineIndex, 1)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn v-if="form.headlines.length < 3" x-small text color="primary" class="mb-3" @click="form.headlines.push('')">
              <v-icon x-small left>mdi-plus</v-icon>Headline-Option
            </v-btn>
            <v-text-field v-model="form.description" label="Beschreibung (optional)" dense outlined />
            <v-row dense>
              <v-col cols="12" sm="5">
                <v-select v-model="form.ctaType" :items="ctaOptions" label="Call to Action" dense outlined />
              </v-col>
              <v-col cols="12" sm="7">
                <v-text-field v-model="form.linkUrl" label="Ziel-URL" dense outlined />
              </v-col>
            </v-row>
            <div class="ad-publish__step-actions">
              <v-btn color="primary" depressed small :disabled="!settingsValid" @click="step = 6">Weiter</v-btn>
            </div>
          </v-stepper-content>

          <!-- 6. Veröffentlichen -->
          <v-stepper-step step="6" :editable="settingsValid">Veröffentlichen</v-stepper-step>
          <v-stepper-content step="6">
            <v-btn-toggle v-if="publishImages.length > 1" v-model="adType" mandatory dense class="mb-3">
              <v-btn small value="variants">{{ publishImages.length }} einzelne Anzeigen</v-btn>
              <v-btn small value="carousel">1 Carousel-Anzeige</v-btn>
            </v-btn-toggle>

            <div class="ad-publish__summary">
              <div v-if="adType === 'carousel' && publishImages.length > 1">
                <strong>1 Carousel-Anzeige</strong> mit {{ publishImages.length }} Karten · Format: {{ formatLabel(format) }}
              </div>
              <div v-else>
                <strong>{{ publishImages.length }}</strong> {{ publishImages.length === 1 ? "Anzeige" : "Anzeigen-Varianten" }} · Format: {{ formatLabel(format) }}
              </div>
              <div class="ad-publish__hint">
                Quelle: {{ selectedFinals.length ? "finalisierte Bilder (GPT Image 2)" : "Bilder aus Schritt 2" }}
                · {{ form.primaryTexts.filter((t) => t.trim()).length }} Texte, {{ form.headlines.filter((t) => t.trim()).length }} Headlines
              </div>
              <div v-if="adsetMode === 'existing'">→ in Adset „{{ selectedAdsetName }}"</div>
              <div v-else>→ neues Adset „{{ form.newAdsetName }}" ({{ form.dailyBudget ? number(form.dailyBudget) + " €/Tag" : "Budget von Kampagne" }}{{ form.roasGoal ? ", ROAS-Ziel " + number(form.roasGoal) : ", Lowest Cost" }})</div>
              <div class="ad-publish__hint mt-1">Alles wird pausiert angelegt — aktiviert wird bewusst im Ads Manager.</div>
            </div>
            <v-alert v-if="publishError" type="error" dense outlined class="mt-2">{{ publishError }}</v-alert>
            <div class="ad-publish__step-actions">
              <v-btn color="primary" depressed :loading="publishing" @click="publish">
                <v-icon left small>mdi-pause-circle-outline</v-icon>
                Pausiert veröffentlichen
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>

      <!-- Lightbox: Großansicht für jedes Bild -->
      <v-dialog v-model="lightbox" max-width="860">
        <v-card light class="ad-publish__lightbox" @click="lightbox = false">
          <img v-if="lightboxSrc" :src="lightboxSrc" />
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { mapGetters } from "vuex";
import { getDecimalNumberString } from "@/utils";
import { buildAdDataPayload } from "@/components/Statistics/StatisticByShops/adDataPayload";

const MAX_FULL_BYTES = 1.8 * 1024 * 1024;
const ANALYSIS_EDGE = 1200;

export default {
  name: "AdPublishDialog",
  props: {
    value: Boolean,
    shopCode: String,
    domain: String,
    insights: {
      type: Object,
      default: null,
    },
    breakEvenRoas: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      step: 1,
      ideaDirection: "",
      productUrl: "",
      productInfo: null,
      ideas: [],
      ideasAnalysis: null,
      ideasLoading: false,
      ideasError: null,
      ideaStageIndex: 0,
      ideaStageTimer: null,
      selectedIdeaIndex: null,
      copiedIndex: null,
      ideaPreviews: {},
      selectedPreviews: [],
      finalizing: false,
      finalizeDone: 0,
      finalizeTarget: 0,
      finalizeError: null,
      finalImages: [],
      selectedFinals: [],
      adType: "variants",
      finalsDragover: false,
      imagesDragover: false,
      lightbox: false,
      lightboxSrc: null,
      images: [],
      imageError: null,
      suggestions: null,
      suggesting: false,
      suggestError: null,
      format: "auto",
      formatOptions: [
        { text: "Automatisch (alle Platzierungen)", value: "auto" },
        { text: "Feed (1:1 / 4:5)", value: "feed" },
        { text: "Story (9:16)", value: "story" },
      ],
      adsetMode: "existing",
      adsets: [],
      adsetsLoading: false,
      form: {
        adsetId: null,
        newAdsetName: "",
        templateAdsetId: null,
        dailyBudget: null,
        roasGoal: null,
        adName: "",
        primaryTexts: [""],
        headlines: [""],
        description: "",
        ctaType: "SHOP_NOW",
        linkUrl: "",
      },
      ctaOptions: [
        { text: "Shop Now", value: "SHOP_NOW" },
        { text: "Order Now", value: "ORDER_NOW" },
        { text: "Learn More", value: "LEARN_MORE" },
        { text: "Get Offer", value: "GET_OFFER" },
        { text: "Kein Button", value: null },
      ],
      publishing: false,
      publishError: null,
      published: null,
    };
  },
  computed: {
    ...mapGetters(["statisticRange"]),
    adsetItems() {
      return this.adsets.map((adset) => ({
        text: `${adset.campaignName} — ${adset.name}${adset.dailyBudget ? ` (${this.number(adset.dailyBudget)} €/Tag)` : ""}`,
        value: adset.id,
      }));
    },
    selectedAdsetName() {
      return this.adsets.find((adset) => adset.id === this.form.adsetId)?.name || "–";
    },
    recommendedAdsetLabel() {
      if (!this.suggestions?.recommendedAdsetId) return null;
      const adset = this.adsets.find((item) => item.id === this.suggestions.recommendedAdsetId);
      return adset ? `${adset.campaignName} — ${adset.name}` : null;
    },
    settingsValid() {
      const adFieldsOk = this.form.adName && this.form.primaryTexts.some((t) => t.trim()) && this.form.linkUrl && this.publishImages.length;
      if (this.adsetMode === "existing") return !!(adFieldsOk && this.form.adsetId);
      return !!(adFieldsOk && this.form.newAdsetName && this.form.templateAdsetId);
    },
    selectedIdea() {
      return this.selectedIdeaIndex === null ? null : this.ideas[this.selectedIdeaIndex] || null;
    },
    // What actually gets published: the selected finals, else the step-2 images.
    publishImages() {
      if (this.selectedFinals.length) {
        return this.selectedFinals.map((index) => this.finalImages[index]).filter(Boolean);
      }
      return this.images;
    },
    // Staged status labels while the (single) ideas request runs — describes
    // what the model is actually doing, advanced on a timer.
    ideaStages() {
      const stages = [];
      if (this.productUrl) stages.push("Produktseite wird gelesen…");
      stages.push("Werbedaten werden analysiert…");
      stages.push("High- & Low-Performer werden ausgewertet…");
      stages.push("Lücken werden identifiziert…");
      stages.push("Ideen werden formuliert…");
      return stages;
    },
    // Idea context handed to the setup-suggestion step.
    combinedIdeaText() {
      const parts = [];
      if (this.selectedIdea) {
        parts.push(`Gewählte Idee: ${this.selectedIdea.titel} (${this.selectedIdea.typ}). Ziel: ${this.selectedIdea.ziel}`);
      }
      if (this.ideaDirection) parts.push(`Nutzer-Vorgabe: ${this.ideaDirection}`);
      return parts.join(" | ") || null;
    },
  },
  beforeDestroy() {
    clearInterval(this.ideaStageTimer);
  },
  watch: {
    value(open) {
      if (open) {
        this.loadAdsets();
        if (!this.form.linkUrl && this.domain) {
          this.form.linkUrl = `https://${this.domain.toLowerCase()}`;
        }
      }
    },
    shopCode() {
      this.resetAll();
      this.adsets = [];
    },
  },
  methods: {
    resetAll() {
      this.step = 1;
      this.ideaDirection = "";
      this.productUrl = "";
      this.productInfo = null;
      this.ideas = [];
      this.ideasAnalysis = null;
      this.ideasError = null;
      this.selectedIdeaIndex = null;
      this.copiedIndex = null;
      this.ideaPreviews = {};
      this.selectedPreviews = [];
      this.finalizing = false;
      this.finalizeDone = 0;
      this.finalizeTarget = 0;
      this.finalizeError = null;
      this.finalImages = [];
      this.selectedFinals = [];
      this.adType = "variants";
      this.images = [];
      this.imageError = null;
      this.suggestions = null;
      this.suggestError = null;
      this.format = "auto";
      this.adsetMode = "existing";
      this.published = null;
      this.publishError = null;
      this.form = {
        adsetId: null,
        newAdsetName: "",
        templateAdsetId: null,
        dailyBudget: null,
        roasGoal: null,
        adName: "",
        primaryTexts: [""],
        headlines: [""],
        description: "",
        ctaType: "SHOP_NOW",
        linkUrl: this.domain ? `https://${this.domain.toLowerCase()}` : "",
      };
    },
    async loadAdsets() {
      if (this.adsets.length) return;
      this.adsetsLoading = true;
      try {
        const list = httpsCallable(functions, "listMetaAdsets", { timeout: 120000 });
        const res = await list({ shopCode: this.shopCode });
        this.adsets = res.data.adsets || [];
      } catch (e) {
        this.publishError = e?.message || "Adsets konnten nicht geladen werden.";
      }
      this.adsetsLoading = false;
    },
    async onImagesSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      await this.addImageFiles(files);
    },
    async onImagesDrop(event) {
      this.imagesDragover = false;
      await this.addImageFiles(Array.from(event.dataTransfer?.files || []));
    },
    async addImageFiles(files) {
      this.imageError = null;
      for (const file of files.slice(0, 3 - this.images.length)) {
        if (!file.type.startsWith("image/")) continue;
        try {
          this.images.push(await this.processImage(file));
        } catch (e) {
          this.imageError = "Bild konnte nicht verarbeitet werden.";
        }
      }
    },
    downloadImage(image, index) {
      const link = document.createElement("a");
      link.href = `data:${image.full.media_type};base64,${image.full.data}`;
      link.download = `${this.form.adName || "ad"}-final-${index + 1}.jpg`;
      link.click();
    },
    async onFinalsSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      await this.addFinalFiles(files);
    },
    async onFinalsDrop(event) {
      this.finalsDragover = false;
      await this.addFinalFiles(Array.from(event.dataTransfer?.files || []));
    },
    // Locally optimized images come back into the finals gallery, pre-selected.
    async addFinalFiles(files) {
      this.finalizeError = null;
      for (const file of files.slice(0, 6 - this.finalImages.length)) {
        if (!file.type.startsWith("image/")) continue;
        try {
          const processed = await this.processImage(file);
          this.finalImages.push(processed);
          if (this.selectedFinals.length < 3) this.selectedFinals.push(this.finalImages.length - 1);
        } catch (e) {
          this.finalizeError = "Bild konnte nicht verarbeitet werden.";
        }
      }
    },
    // full: near-original quality for publishing (downscaled only if too heavy)
    // small: 1200px preview for the AI analysis
    renderVariants(img) {
      const render = (edge, quality) => {
        const scale = Math.min(1, edge / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL("image/jpeg", quality).split(",")[1];
      };

      let fullData = render(Infinity, 0.92);
      if (fullData.length * 0.75 > MAX_FULL_BYTES) fullData = render(2048, 0.85);

      return {
        full: { media_type: "image/jpeg", data: fullData },
        small: { media_type: "image/jpeg", data: render(ANALYSIS_EDGE, 0.8) },
        width: img.width,
        height: img.height,
      };
    },
    processImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(this.renderVariants(img));
        };
        img.onerror = reject;
        img.src = url;
      });
    },
    async generateIdeas() {
      this.ideasLoading = true;
      this.ideasError = null;
      this.selectedIdeaIndex = null;
      this.selectedPreview = null;
      this.ideaPreviews = {};
      // Advance the staged status display while the request runs.
      this.ideaStageIndex = 0;
      clearInterval(this.ideaStageTimer);
      this.ideaStageTimer = setInterval(() => {
        if (this.ideaStageIndex < this.ideaStages.length - 1) this.ideaStageIndex++;
      }, 6000);
      try {
        const suggestIdeas = httpsCallable(functions, "suggestAdIdeas", { timeout: 300000 });
        const res = await suggestIdeas({
          direction: this.ideaDirection || null,
          productUrl: this.productUrl || null,
          adData: buildAdDataPayload(this.insights),
          shopContext: {
            shop: this.shopCode,
            zeitraum: { von: this.statisticRange.min, bis: this.statisticRange.max },
            breakEvenRoas: this.breakEvenRoas,
          },
        });
        this.ideas = res.data.ideen || [];
        this.ideasAnalysis = res.data.analyse || null;
        this.productInfo = res.data.productInfo || null;
      } catch (e) {
        this.ideasError = e?.message || "Ideen konnten nicht generiert werden.";
      }
      clearInterval(this.ideaStageTimer);
      this.ideasLoading = false;
    },
    selectIdea(index) {
      if (this.selectedIdeaIndex === index) {
        this.selectedIdeaIndex = null;
        return;
      }
      this.selectedIdeaIndex = index;
      // Drafts are generated on demand, only for the chosen idea.
      if (!this.ideaPreviews[index]) this.generatePreviews(index);
    },
    async generatePreviews(index) {
      this.$set(this.ideaPreviews, index, { images: [], loading: true, adding: false, error: null });
      const entry = this.ideaPreviews[index];
      const generate = httpsCallable(functions, "generateIdeaPreviews", { timeout: 300000 });
      // Three single-image calls so thumbnails appear as soon as each is ready.
      const calls = Array.from({ length: 3 }, () =>
        generate({ prompt: this.ideas[index].bildPrompt, productInfo: this.productInfo, count: 1 })
          .then((res) => entry.images.push(...(res.data.images || [])))
          .catch((e) => { entry.error = e?.message || "Entwurf fehlgeschlagen."; })
      );
      await Promise.allSettled(calls);
      entry.loading = false;
      if (entry.images.length) entry.error = null;
    },
    // Plus tile in step 1: generate one additional draft for this idea.
    async addPreview(index) {
      const entry = this.ideaPreviews[index];
      if (!entry || entry.adding || entry.loading) return;
      entry.adding = true;
      entry.error = null;
      try {
        const generate = httpsCallable(functions, "generateIdeaPreviews", { timeout: 300000 });
        const res = await generate({ prompt: this.ideas[index].bildPrompt, productInfo: this.productInfo, count: 1 });
        entry.images.push(...(res.data.images || []));
      } catch (e) {
        entry.error = e?.message || "Entwurf fehlgeschlagen.";
      }
      entry.adding = false;
    },
    isPreviewSelected(ideaIndex, imageIndex) {
      return this.selectedPreviews.includes(`${ideaIndex}-${imageIndex}`);
    },
    togglePreview(ideaIndex, imageIndex) {
      const key = `${ideaIndex}-${imageIndex}`;
      const position = this.selectedPreviews.indexOf(key);
      if (position >= 0) {
        this.selectedPreviews.splice(position, 1);
      } else if (this.selectedPreviews.length < 3) {
        this.selectedPreviews.push(key);
        this.selectedIdeaIndex = ideaIndex;
      }
    },
    // Sync the chosen drafts into step 2's image list (uploads stay untouched).
    async proceedFromIdeas() {
      this.images = this.images.filter((image) => !image.draftKey || this.selectedPreviews.includes(image.draftKey));
      for (const key of this.selectedPreviews) {
        if (this.images.length >= 3) break;
        if (this.images.some((image) => image.draftKey === key)) continue;
        const [ideaIndex, imageIndex] = key.split("-").map(Number);
        const preview = this.ideaPreviews[ideaIndex]?.images?.[imageIndex];
        if (!preview) continue;
        const processed = await this.processDataUrl(preview);
        processed.draftKey = key;
        this.images.push(processed);
      }
      this.step = 2;
    },
    async generateOneFinal(referenceImage) {
      const generate = httpsCallable(functions, "generateFinalAdImage", { timeout: 300000 });
      const basePrompt = this.selectedIdea?.bildPrompt
        || "Erstelle aus dem Referenzbild eine finale, hochwertige Werbeanzeigen-Grafik.";
      const res = await generate({
        prompt: basePrompt,
        feedback: this.suggestions?.creativeFeedback || null,
        productInfo: this.productInfo,
        format: this.format,
        referenceImage,
      });
      return await this.processDataUrl(res.data.image);
    },
    // Applies the creative feedback to every step-2 image — results appear as
    // a selectable gallery; the selection is what gets published.
    async finalizeImages() {
      if (!this.suggestions || !this.images.length) return;
      this.finalizing = true;
      this.finalizeError = null;
      this.finalizeDone = 0;
      this.finalizeTarget = this.images.length;
      try {
        for (let index = 0; index < this.images.length; index++) {
          const processed = await this.generateOneFinal(this.images[index].full);
          this.finalImages.push(processed);
          this.selectedFinals.push(this.finalImages.length - 1);
          this.finalizeDone = index + 1;
        }
      } catch (e) {
        this.finalizeError = e?.message || "Finalisierung fehlgeschlagen.";
      }
      this.finalizing = false;
    },
    // Plus tile: one additional final variant (cycles through the source images).
    async addFinalVariant() {
      if (this.finalizing || !this.images.length) return;
      this.finalizing = true;
      this.finalizeError = null;
      this.finalizeDone = this.finalImages.length;
      this.finalizeTarget = this.finalImages.length + 1;
      try {
        const source = this.images[this.finalImages.length % this.images.length];
        const processed = await this.generateOneFinal(source.full);
        this.finalImages.push(processed);
        this.selectedFinals.push(this.finalImages.length - 1);
      } catch (e) {
        this.finalizeError = e?.message || "Generierung fehlgeschlagen.";
      }
      this.finalizing = false;
    },
    toggleFinal(index) {
      const position = this.selectedFinals.indexOf(index);
      if (position >= 0) this.selectedFinals.splice(position, 1);
      else if (this.selectedFinals.length < 3) this.selectedFinals.push(index);
    },
    openLightbox(image) {
      this.lightboxSrc = `data:${image.media_type};base64,${image.data}`;
      this.lightbox = true;
    },
    processDataUrl(image) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(this.renderVariants(img));
        img.onerror = reject;
        img.src = `data:${image.media_type};base64,${image.data}`;
      });
    },
    async copyPrompt(idea, index) {
      try {
        await navigator.clipboard.writeText(idea.bildPrompt);
        this.copiedIndex = index;
        setTimeout(() => { this.copiedIndex = null; }, 2000);
      } catch (e) {
        this.ideasError = "Kopieren fehlgeschlagen — Prompt bitte manuell markieren.";
      }
    },
    async runSuggestions() {
      this.suggesting = true;
      this.suggestError = null;
      await this.loadAdsets();
      try {
        const suggest = httpsCallable(functions, "suggestAdSetup", { timeout: 300000 });
        const res = await suggest({
          images: this.images.map((image) => image.small),
          ideaText: this.combinedIdeaText,
          adData: buildAdDataPayload(this.insights),
          adsets: this.adsets,
          shopContext: {
            shop: this.shopCode,
            zeitraum: { von: this.statisticRange.min, bis: this.statisticRange.max },
            breakEvenRoas: this.breakEvenRoas,
          },
        });
        this.applySuggestions(res.data.suggestions);
      } catch (e) {
        this.suggestError = e?.message || "Analyse fehlgeschlagen.";
      }
      this.suggesting = false;
    },
    applySuggestions(suggestions) {
      this.suggestions = suggestions;
      this.format = suggestions.format || "auto";
      this.adsetMode = suggestions.adsetMode || "existing";
      this.form.adName = suggestions.adName || this.form.adName;
      if (suggestions.primaryTexts?.length) {
        this.form.primaryTexts = suggestions.primaryTexts.slice(0, 5);
      }
      const suggestedHeadlines = suggestions.headlines?.length
        ? suggestions.headlines
        : suggestions.headline ? [suggestions.headline] : null;
      if (suggestedHeadlines) this.form.headlines = suggestedHeadlines.slice(0, 3);
      this.form.description = suggestions.description || this.form.description;
      this.form.ctaType = suggestions.ctaType || this.form.ctaType;
      if (suggestions.recommendedAdsetId && this.adsets.some((adset) => adset.id === suggestions.recommendedAdsetId)) {
        this.form.adsetId = suggestions.recommendedAdsetId;
      }
      if (suggestions.newAdset) {
        this.form.newAdsetName = suggestions.newAdset.name || "";
        if (this.adsets.some((adset) => adset.id === suggestions.newAdset.templateAdsetId)) {
          this.form.templateAdsetId = suggestions.newAdset.templateAdsetId;
        }
        this.form.dailyBudget = suggestions.newAdset.dailyBudget || null;
        this.form.roasGoal = suggestions.newAdset.roasGoal || null;
      }
    },
    async publish() {
      this.publishing = true;
      this.publishError = null;
      try {
        const publishAd = httpsCallable(functions, "publishMetaAd", { timeout: 180000 });
        const res = await publishAd({
          shopCode: this.shopCode,
          mode: this.adsetMode,
          adsetId: this.adsetMode === "existing" ? this.form.adsetId : null,
          newAdset: this.adsetMode === "new" ? {
            templateAdsetId: this.form.templateAdsetId,
            name: this.form.newAdsetName,
            dailyBudget: this.form.dailyBudget || null,
            roasGoal: this.form.roasGoal || null,
          } : null,
          adName: this.form.adName,
          images: this.publishImages.map((image) => image.full),
          adType: this.adType,
          primaryTexts: this.form.primaryTexts.filter((t) => t.trim()),
          headlines: this.form.headlines.filter((t) => t.trim()),
          description: this.form.description || null,
          ctaType: this.form.ctaType,
          linkUrl: this.form.linkUrl,
        });
        this.published = res.data;
      } catch (e) {
        this.publishError = e?.message || "Anlegen fehlgeschlagen.";
      }
      this.publishing = false;
    },
    formatLabel(value) {
      return this.formatOptions.find((option) => option.value === value)?.text || value;
    },
    number(value, fraction = 2) {
      return getDecimalNumberString(value, fraction);
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-publish__title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.ad-publish__subtitle {
  font-size: 13px;
  font-weight: lighter;
  color: #757575;
}

.ad-publish__stepper {
  box-shadow: none !important;
  padding-bottom: 8px;

  ::v-deep .v-stepper__step {
    padding: 16px 24px 8px;
  }

  ::v-deep .v-stepper__label {
    font-weight: 600;

    small {
      color: #757575;
      font-weight: 400;
    }
  }

  ::v-deep .v-stepper__content {
    margin-left: 35px !important;
    border-left: 1px solid #e8e8e8 !important;
    padding-top: 4px;
  }
}

.ad-publish__step-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.ad-publish__hint {
  font-size: 12px;
  color: #9e9e9e;
}

.ad-publish__pull-up {
  margin-top: -4px;
  margin-bottom: 8px;
}

.ad-publish__images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ad-publish__thumb {
  position: relative;

  img {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 12px;
    border: 2px solid #ececec;
    display: block;
    cursor: zoom-in;
    transition: border-color 0.15s;

    &:hover {
      border-color: #b8cdea;
    }
  }
}

.ad-publish__thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: white !important;
  border: 1px solid #dddddd;
}

.ad-publish__dropzone {
  width: 120px;
  height: 120px;
  border: 2px dashed #d0d0d0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9e9e9e;
  font-size: 11px;
  gap: 2px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #1c6ebb;
  }
}

.ad-publish__feedback {
  font-size: 13px;
}

.ad-publish__progress {
  margin-top: 14px;
  border: 1px solid #e6e0f5;
  background: linear-gradient(180deg, #faf9ff 0%, #f5f3fc 100%);
  border-radius: 12px;
  padding: 14px 16px;
}

.ad-publish__progress--previews {
  border-color: #d5e3f5;
  background: linear-gradient(180deg, #f8fbff 0%, #f0f6fd 100%);
}

.ad-publish__progress-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #b0b0b0;
  padding: 4px 0;
  transition: color 0.3s;

  &--done {
    color: #616161;
  }

  &--active {
    color: #1c1c1c;
    font-weight: 600;
  }
}

.ad-publish__analysis {
  font-size: 12px;
  color: #616161;
  background-color: #f7f5fc;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}

.ad-publish__idea {
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: #b8cdea;
  }

  &--selected {
    border-color: #1c6ebb;
    background-color: #f4f8fd;
  }
}

.ad-publish__idea-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-publish__idea-title {
  font-weight: 700;
  font-size: 13px;
  flex: 1;
}

.ad-publish__idea-goal {
  font-size: 12px;
  margin: 4px 0 2px;
}

.ad-publish__previews {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.ad-publish__preview {
  position: relative;
  cursor: pointer;
  border: 2px solid #ececec;
  border-radius: 12px;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  overflow: hidden;

  img {
    width: 120px;
    height: 120px;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: #b8cdea;
    box-shadow: rgba(28, 110, 187, 0.12) 0 4px 12px;
    transform: translateY(-1px);
  }

  &--selected {
    border-color: #1c6ebb;
    box-shadow: rgba(28, 110, 187, 0.18) 0 4px 12px;
  }

  &--loading,
  &--add {
    width: 120px;
    height: 120px;
    border: 2px dashed #d0d0d0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 11px;
    color: #9e9e9e;
    background-color: #fafafa;
  }

  &--loading {
    cursor: default;
  }

  &--add:hover {
    border-color: #1c6ebb;
    color: #1c6ebb;
    box-shadow: none;
    transform: none;
  }
}

.ad-publish__preview-check {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: white;
  border-radius: 50%;
}

.ad-publish__zoom {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background-color: rgba(255, 255, 255, 0.9) !important;
}

.ad-publish__download {
  position: absolute;
  bottom: 4px;
  left: 4px;
  background-color: rgba(255, 255, 255, 0.9) !important;
}

.ad-publish__previews--dragover {
  outline: 2px dashed #1c6ebb;
  outline-offset: 4px;
  border-radius: 8px;
}

.ad-publish__field-label {
  font-weight: 700;
  font-size: 13px;
  margin: 10px 0 6px;

  .ad-publish__hint {
    font-weight: 400;
    margin-left: 6px;
  }
}

.ad-publish__multi-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;

  > :first-child {
    flex: 1;
  }

  .v-btn {
    margin-top: 10px;
  }
}

.ad-publish__finalize-title {
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 6px;
}

.ad-publish__lightbox {
  cursor: zoom-out;
  padding: 8px;
  border-radius: 12px !important;

  img {
    width: 100%;
    display: block;
    border-radius: 8px;
  }
}

.ad-publish__idea-prompt {
  margin-top: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11px;
  font-family: monospace;
  color: #444444;
  cursor: text;
}

.ad-publish__idea-prompt-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Roboto, sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #757575;
  margin-bottom: 4px;
}

.ad-publish__finalize {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #d5e3f5;
  background-color: #f7fafd;
  border-radius: 10px;
}

.ad-publish__summary {
  font-size: 13px;
  line-height: 1.7;
}

.ad-publish__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 16px;
}
</style>
